import { useTranslations } from "@zapier/i18n";
import { useTableConnectedAssets, type HydratedTable } from "@zapier/client";

import {
  Button,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogRoot,
  SkeletonLoader,
  SkeletonText,
  Spacer,
  Text,
} from "@zapier/design-system";

import { useDeleteTable } from "./useDeleteTable";
import { useEmitTableDeleteModalEvent } from "@zapier/shared/src/events";
import { FlexSpacer, LinkedAssetsList } from "@zapier/shared";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  table: HydratedTable | undefined;
  folderId?: string;
}

export function TableDeleteModal({ isOpen, onClose, table, folderId }: Props) {
  const t = useTranslations("table-delete-modal");

  const { deleteTable, isPending } = useDeleteTable(table, folderId);
  const { emit } = useEmitTableDeleteModalEvent();

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      emit("cancel");
      onClose();
    }
  };

  const {
    transformedAssets,
    hasBlockingConnections,
    hasNonBlockingConnections,
    hasViews,
    isFetching: isFetchingAssets,
  } = useTableConnectedAssets(isOpen ? table?.id : undefined);

  const isView = table?.kind === "virtual_table";

  const modalTitle = hasBlockingConnections
    ? t("headers.unable-to-delete-table", { tableName: table?.name ?? "" })
    : t("headers.delete-table", {
        tableName: table?.name ?? "",
        isView: String(isView),
      });
  const modalText = hasBlockingConnections
    ? t("messages.blocking-connections-warning")
    : hasNonBlockingConnections
      ? t("messages.non-blocking-connections-warning")
      : hasViews
        ? t("messages.permanent-deletion-with-views-warning")
        : t("messages.permanent-deletion-warning", { isView: String(isView) });

  if (!table) {
    return;
  }

  return (
    <ModalDialogRoot isOpen={isOpen} onOpenChange={handleOpenChange}>
      <>
        <ModalDialogHeader>{modalTitle}</ModalDialogHeader>

        <ModalDialogBody>
          {isFetchingAssets && (
            <SkeletonLoader>
              <SkeletonText lines={2} width="100%" />
            </SkeletonLoader>
          )}
          {!isFetchingAssets && (
            <>
              <Text color="TextWeaker">{modalText}</Text>
              {(hasBlockingConnections ||
                hasNonBlockingConnections ||
                hasViews) && (
                <>
                  <Spacer height={12} />
                  <LinkedAssetsList linkedAssets={transformedAssets} />
                </>
              )}
            </>
          )}
        </ModalDialogBody>

        <ModalDialogFooter>
          <Button
            isDisabled={isPending}
            onPress={() => {
              emit("cancel");
              onClose();
            }}
            variant="tertiary"
          >
            {t("buttons.cancel")}
          </Button>

          <FlexSpacer />

          <Button
            isDisabled={isPending || hasBlockingConnections}
            isPending={isPending || isFetchingAssets}
            onPress={() => {
              emit("confirm");
              deleteTable();
              onClose();
            }}
            variant="danger"
          >
            {t("buttons.delete", { isView: String(isView) })}
          </Button>
        </ModalDialogFooter>
      </>
    </ModalDialogRoot>
  );
}

// For ease of use during `dynamic` import
export default TableDeleteModal;
